import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  tap,
  take,
  filter,
  map,
  catchError,
  of,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from 'src/app/shared/models/interfaces';
import { Router } from '@angular/router';
import { SnackbarService } from '../services/snackbar/snackbar.service';
import { UserService } from '../services/user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authUrl = `${environment.api}/auth`;
  private readonly USER_ID_KEY = 'userId';

  // Deprecated JWT authentication - commented out for potential future reintegration.
  // For the moment, we decided to only user Switch-Authentication
  // for the first version at least.
  // private readonly TOKEN_KEY = 'access_token';

  private _activeUser$ = new BehaviorSubject<User | undefined>(undefined);
  activeUser$ = this._activeUser$.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackbarService: SnackbarService,
    private userService: UserService
  ) {}

  getUserId(): number | null {
    const userId = localStorage.getItem(this.USER_ID_KEY);
    return userId ? parseInt(userId, 10) : null;
  }

  // saveUserId(userId: number): void {
  //   localStorage.setItem(this.USER_ID_KEY, userId.toString());
  // }

  getActiveUser(): Observable<User> {
    if (this._activeUser$.getValue() !== undefined) {
      return this.activeUser$.pipe(
        filter((user): user is User => user !== undefined)
      );
    }
    return this.http.get<User>(`${this.authUrl}/me`).pipe(
      tap((user) => this._activeUser$.next(user)),
      take(1)
    );
  }

  // isOidcUser is set to false in the backend if the user has no SwitchEduId
  // This is to avoid sending the Switch Id
  isUserOidc(): Observable<boolean> {
    return this.getActiveUser().pipe(
      map((user) => !!user.isOidcUser),
      catchError(() => of(false))
    );
  }

  // auth "me" endpoint is guarded -> unauthenticated requests will return 401
  // Other errors can occur if email already exist or when oidc authentication fails
  checkSession(): Observable<User | null> {
    return this.http
      .get<User>(`${this.authUrl}/me`, { withCredentials: true })
      .pipe(
        take(1),
        tap((user) => {
          if (user) {
            this._activeUser$.next(user);
          }
        })
      );
  }

  async hasRole(role: string): Promise<boolean> {
    const user = this._activeUser$.getValue();
    return user?.role === role;
  }

  // Backend handles login with oidc
  loginWithOidc() {
    window.location.replace(`${environment.api}/auth/oidc-login`);
  }

  logout(): void {
    this._activeUser$.next(undefined);

    // Deprecated JWT authentication - commented out for potential future reintegration.
    // For the moment, we decided to only user Switch-Authentication
    // for the first version at least.
    // If user was logged in with jwt-auth, only need to remove details from local storage
    // if (localStorage.getItem(this.TOKEN_KEY)) {
    //   localStorage.removeItem(this.TOKEN_KEY);
    //   localStorage.removeItem(this.USER_ID_KEY);
    // } else {
    // If user has used oidc-auth, backend cleans up session
    localStorage.clear();
    window.location.replace(`${environment.api}/auth/oidc-logout`);
    // }
    this.router.navigate(['/']);
  }

  onLogoutClick() {
    this.logout();
    this.snackbarService.showSuccessMessage('Logged out successfully');
  }

  hasUserConsented(): Observable<boolean> {
    if (this._activeUser$.getValue() !== undefined) {
      // If we have an active user, check if they have consented and store in local storage
      return this.activeUser$.pipe(map((user) => !!user?.hasGivenConsent));
    }
    // If no active user, return false
    return of(false);
  }

  hasUserSkippedGroupJoining(): Observable<boolean> {
    return this.activeUser$.pipe(map((user) => !!user?.hasSkippedGroupJoining));
  }

  submitUserConsent(): Observable<any> {
    return this.http.put(`${this.authUrl}/consent`, { consentGiven: true });
  }

  deleteCurrentUser(): Observable<any> {
    return this.userService.anonymizeUsersPersonalData().pipe(
      tap(() => {
        this.logout();
      })
    );
  }

  // Deprecated JWT authentication - commented out for potential future reintegration.
  // For the moment, we decided to only user Switch-Authentication for
  // the first version at least.

  // register(user: Partial<User>): Observable<any> {
  //   return this.http.post<any>(`${this.authUrl}/jwt-register`, user);
  // }

  // login( password: string): Observable<any> {
  //   return this.http.post<any>(`${this.authUrl}/jwt-login`, {
  //     password,
  //   });
  // }
  // isLoggedIn(): boolean {
  //   const token = this.getToken();
  //   if (!token) return false;

  //   const payload = this.parseJwt(token);
  //   if (!payload?.exp) return false;
  //   return new Date(payload.exp * 1000) > new Date();
  // }
  // private parseJwt(token: string): any | null {
  //   try {
  //     return JSON.parse(atob(token.split('.')[1]));
  //   } catch (e) {
  //     return null;
  //   }
  // }
  //   saveToken(token: string): void {
  //   localStorage.setItem(this.TOKEN_KEY, token);
  // }
  //   getToken(): string | null {
  //   return localStorage.getItem(this.TOKEN_KEY);
  // }
}
